import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UIMessage } from "ai";
import { useRef } from "react";
import { chatHistoryKeys } from "../query-keys";

export type ChatHistoryItem = {
  id: string;
  title: string;
  createdAt?: string;
  updatedAt?: string;
};

type ChatHistoryApi = {
  fetchHistory: () => Promise<ChatHistoryItem[]>;
  fetchMessages: (chatId: string) => Promise<UIMessage[]>;
  deleteChat: (chatId: string) => Promise<void>;
  deleteAll: () => Promise<void>;
};

export class ChatApiError extends Error {
  status: number;
  method: string;
  url: string;

  constructor({
    status,
    method,
    url,
  }: {
    status: number;
    method: string;
    url: string;
  }) {
    super(`${method} ${url}: ${status}`);
    this.name = "ChatApiError";
    this.status = status;
    this.method = method;
    this.url = url;
  }
}

const chatHistoryApiCache = new Map<string, ChatHistoryApi>();

function createChatHistoryApi(baseUrl: string): ChatHistoryApi {
  const opts: RequestInit = { credentials: "include" };

  async function request<T>(url: string, init?: RequestInit): Promise<T> {
    const method = init?.method ?? "GET";
    const res = await fetch(url, { ...opts, ...init });
    if (!res.ok) {
      throw new ChatApiError({ status: res.status, method, url });
    }
    if (res.status === 204 || res.headers.get("content-length") === "0") {
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      return undefined as T;
    }
    return res.json();
  }

  return {
    fetchHistory: () => request<ChatHistoryItem[]>(`${baseUrl}/api/history`),
    fetchMessages: (chatId) =>
      request<UIMessage[]>(`${baseUrl}/api/chat/${chatId}/messages`),
    deleteChat: async (chatId) => {
      await request(`${baseUrl}/api/chat/${chatId}`, { method: "DELETE" });
    },
    deleteAll: async () => {
      await request(`${baseUrl}/api/history`, { method: "DELETE" });
    },
  };
}

export function getChatHistoryApi(baseUrl: string): ChatHistoryApi {
  const existing = chatHistoryApiCache.get(baseUrl);
  if (existing) {
    return existing;
  }

  const api = createChatHistoryApi(baseUrl);
  chatHistoryApiCache.set(baseUrl, api);
  return api;
}

type UseChatHistoryMutationsOptions = {
  baseUrl: string;
  viewerId: string;
  onAuthError?: () => void;
};

export function isAuthError(error: unknown): boolean {
  if (error instanceof ChatApiError && error.status === 401) {
    return true;
  }
  // DefaultChatTransport throws plain Error with the response body text on
  // non-OK responses, so we fall back to string matching for streaming 401s.
  return error instanceof Error && error.message.trim() === "Unauthorized";
}

export function useChatHistoryMutations({
  baseUrl,
  viewerId,
  onAuthError,
}: UseChatHistoryMutationsOptions) {
  const onAuthErrorRef = useRef(onAuthError);
  onAuthErrorRef.current = onAuthError;

  const api = getChatHistoryApi(baseUrl);
  const queryClient = useQueryClient();
  const listKey = chatHistoryKeys.list(viewerId);

  function buildDeleteMutation<TVars>({
    mutationFn,
    messagesKey,
    nextList,
  }: {
    mutationFn: (vars: TVars) => Promise<void>;
    messagesKey: (vars: TVars) => readonly unknown[];
    nextList: (
      old: ChatHistoryItem[] | undefined,
      vars: TVars
    ) => ChatHistoryItem[];
  }) {
    return {
      mutationFn,
      meta: { invalidates: [listKey] },
      onMutate: async (vars: TVars) => {
        const msgsKey = messagesKey(vars);
        await Promise.all([
          queryClient.cancelQueries({ queryKey: listKey }),
          queryClient.cancelQueries({ queryKey: msgsKey }),
        ]);
        const snapshot = queryClient.getQueryData<ChatHistoryItem[]>(listKey);
        queryClient.setQueryData<ChatHistoryItem[]>(listKey, (old) =>
          nextList(old, vars)
        );
        queryClient.removeQueries({ queryKey: msgsKey });
        return { snapshot };
      },
      onError: (
        error: unknown,
        _vars: TVars,
        context: { snapshot: ChatHistoryItem[] | undefined } | undefined
      ) => {
        if (context?.snapshot) {
          queryClient.setQueryData(listKey, context.snapshot);
        }
        if (isAuthError(error)) {
          onAuthErrorRef.current?.();
        }
      },
      onSettled: () => queryClient.invalidateQueries({ queryKey: listKey }),
    };
  }

  const deleteMutation = useMutation(
    buildDeleteMutation<string>({
      mutationFn: api.deleteChat,
      messagesKey: (chatId) => chatHistoryKeys.messages(viewerId, chatId),
      nextList: (old, chatId) => old?.filter((h) => h.id !== chatId) ?? [],
    })
  );

  const deleteAllMutation = useMutation(
    buildDeleteMutation<void>({
      mutationFn: api.deleteAll,
      messagesKey: () => chatHistoryKeys.messagesAll(viewerId),
      nextList: () => [],
    })
  );

  return {
    deleteChatAsync: deleteMutation.mutateAsync,
    deleteAllAsync: deleteAllMutation.mutateAsync,
  };
}
